import type { PublicLegal } from '@zetter/shared';
import { ref } from 'vue';
import type { PublicPricing } from '../api/index.js';

/**
 * Публичные данные витрины: линейка планов и реквизиты продавца.
 *
 * Один модуль на все четыре публичные страницы. Раньше тот же запрос стоял
 * отдельным `onMounted` в лендинге, оферте, правилах платежей и политике — при
 * переходе между документами он уходил заново, хотя ответ один и тот же.
 *
 * Но собрано это не ради экономии запроса. Данные обязаны попадать в РАЗМЕТКУ,
 * а не дорисовываться после загрузки бандла: пререндер (`prerender.mjs`) кладёт
 * их сюда до отрисовки и сериализует в `window.__ZETTER_PUBLIC__`, откуда
 * клиент поднимает их ещё до гидратации. Без этого сервер и клиент разошлись бы
 * на первой же цифре, а краулеры, которые JavaScript не исполняют вовсе —
 * GPTBot, ClaudeBot, PerplexityBot, CCBot, — прочитали бы прочерки вместо цен и
 * ИНН.
 *
 * Свой `fetch` вместо `zetterApi`: клиент API тянет за собой контракты всего
 * кабинета, а витрине нужны два публичных ответа. Разница видна в весе бандла,
 * который грузит человек, пришедший из поиска.
 */
/**
 * Шаблон в публичном каталоге.
 *
 * Ровно то, что и так написано на карточке в кабинете. `versionId` нужен не
 * для красоты: по нему строится адрес живого превью, и превью показывает
 * именно ту версию, которую человек видит, а не ту, что опубликуют завтра.
 */
export interface PublicTemplate {
  slug: string;
  name: string;
  description: string | null;
  docType: string | null;
  category: string | null;
  blockCount: number | null;
  versionId: string | null;
}

export interface PublicData {
  pricing: PublicPricing | null;
  legal: PublicLegal | null;
  /** Каталог шаблонов. Пусто — витрина обходится без раздела, а не ломается. */
  templates: PublicTemplate[];
}

declare global {
  interface Window {
    /** Данные, вшитые пререндером в разметку. Появляются до загрузки модулей. */
    __ZETTER_PUBLIC__?: PublicData;
  }
}

const preloaded = typeof window === 'undefined' ? undefined : window.__ZETTER_PUBLIC__;

export const pricing = ref<PublicPricing | null>(preloaded?.pricing ?? null);
export const legal = ref<PublicLegal | null>(preloaded?.legal ?? null);
export const templates = ref<PublicTemplate[]>(preloaded?.templates ?? []);

/**
 * Подстановка данных до отрисовки.
 *
 * Состояние модульное, а не привязанное к экземпляру приложения, и это
 * осознанно: серверная отрисовка здесь — шаг сборки, а не сервер. Страницы
 * рендерятся по одной в одном процессе, делить состояние не с кем. Если
 * когда-нибудь отсюда вырастет SSR под нагрузкой, состояние придётся унести в
 * `app.provide()` — иначе запросы разных людей увидят чужие данные.
 */
export function setPublicData(data: PublicData): void {
  pricing.value = data.pricing;
  legal.value = data.legal;
  templates.value = data.templates;
}

/**
 * Догрузка на клиенте.
 *
 * Нужна ровно тогда, когда пререндер шёл без доступа к API: страница уже
 * отрисована, но без цифр. Пришли данные с разметкой — запроса не будет вовсе.
 */
export async function loadPublicData(): Promise<void> {
  if (pricing.value && legal.value) return;

  const fetched = await fetchPublicData();
  // Порознь: без цен лендинг остаётся лендингом, без реквизитов — тоже.
  // Связывать их одним условием значило бы терять оба из-за одного отказа.
  if (!pricing.value) pricing.value = fetched.pricing;
  if (!legal.value) legal.value = fetched.legal;
  if (templates.value.length === 0) templates.value = fetched.templates;
}

/**
 * Чтение публичных ответов API.
 *
 * `base` пустой — запрос с той же страницы (клиент). Пререндер зовёт эту же
 * функцию с абсолютным адресом: реализация обязана быть одна, иначе разметка и
 * догрузка однажды разойдутся в разборе ответа.
 */
export async function fetchPublicData(base = '', timeoutMs = 10_000): Promise<PublicData> {
  const [pricingData, legalData, catalog] = await Promise.all([
    get<PublicPricing>(`${base}/api/public/plans`, timeoutMs),
    get<PublicLegal>(`${base}/api/public/legal`, timeoutMs),
    get<{ templates: PublicTemplate[] }>(`${base}/api/public/templates`, timeoutMs),
  ]);

  return { pricing: pricingData, legal: legalData, templates: catalog?.templates ?? [] };
}

async function get<T>(url: string, timeoutMs: number): Promise<T | null> {
  try {
    const response = await fetch(url, {
      headers: { accept: 'application/json' },
      signal: AbortSignal.timeout(timeoutMs),
    });
    if (!response.ok) return null;
    const body = (await response.json()) as { data?: T };
    return body.data ?? null;
  } catch {
    // Витрина без цен остаётся витриной. Отказ здесь не должен ронять ни
    // страницу, ни сборку.
    return null;
  }
}
